import { CdkScrollable, CdkVirtualScrollViewport, ScrollDispatcher } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { MatLegacyCheckboxClickAction as MatCheckboxClickAction, MatLegacyCheckboxDefaultOptions as MatCheckboxDefaultOptions, MAT_LEGACY_CHECKBOX_DEFAULT_OPTIONS as MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/legacy-checkbox';
import { MatLegacyOption as MatOption, MatLegacyOptionSelectionChange as MatOptionSelectionChange } from '@angular/material/legacy-core';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { MultiselectDropdownComponent, ServerSideOptions } from '@niceltd/cxone-components/multiselect-dropdown';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { CVLocalizationService } from '../services/cv-localization.service';
import { CvMultiSelectSelectionChangedArgs, CXOneMultiSelectChanged, CXOneMultiSelectOption } from './multi-select.models';
import { FilterTextPipe } from '../pipes/filter-text.pipe';

@Component({
    selector: 'cv-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    providers: [{provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions}]
})
export class MultiSelectComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() id: string;
    @Input() disabled = false;
    @Input() search = true;
    @Input() placeholder = undefined;
    @Input() value: any[];
    @Input() options: any[];
    @Input() autoWidth = false;
    @Input() isLoading = false;
    @Input() primaryKey: string;
    @Input() label: string;
    @Input() selectionText: string;
    @Input() bulkOperations = true;
    @Input() selectAllOnEmpty = false;
    @Input() showEmptyState = true;
    @Input() groupNameExist: boolean;
    @Input() email: string;
    @Input() team: Map<number, string>;
    @Input() showSelectedToggle: boolean;
    @Input() overwritePlaceholder:boolean = true;
    @Input() customStyle: {[key: string] : any};
    @Input() dropdownLabel: string;
    @Input() showSelectAllOrNoneButtons = false;
    @Input() maxSelectionLimit: number;
    @Input() isInfiniteDropdown = false;

    @Output() opened = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();
    @Output() valueChange = new EventEmitter<any[]>();
    @Output() selectionChanged = new EventEmitter<CvMultiSelectSelectionChangedArgs>();

    @ViewChild(MultiselectDropdownComponent) cxOneMultiSelectComponent: MultiselectDropdownComponent;
    @ViewChild(MatSelect) matSelectComponent: MatSelect;

    // mat-select components to interact with
    @ViewChildren(MatSelect) matSelects: QueryList<MatSelect>;
    @ViewChildren(MatOption) matOptions: QueryList<MatOption>;
    @ViewChildren(CdkVirtualScrollViewport) cdkVirtualScrollViewPorts: QueryList<CdkVirtualScrollViewport>;

    filterText = '';
    updatedValues: any[] = [];
    cxValues: CXOneMultiSelectOption[];
    cxOptions: CXOneMultiSelectOption[];
    checkBoxStatus: MatCheckboxDefaultOptions;
    checked: boolean;
    dataLimit = -1;
    skip = 0;
    top = 100;
    dropdownOptions = [];
    serverSideOptions: ServerSideOptions = {
        keyStrokeDelay: 1000,
        resultsCount: 200,
        enabled: true
    };

    get minBufferPx() {
        return this.groupNameExist ? 100 : 200;
    }
    get maxBufferPx() {
        return this.groupNameExist ? 200 : 400;
    }

    private _scrollableSubscription: Subscription;
    private _matSelectSubscription: Subscription;
    private lastChange: CXOneMultiSelectChanged = null;

    constructor(
        private changeDetector: ChangeDetectorRef,
        readonly scrollDispatcher: ScrollDispatcher,
        public config: CvConfigurationService,
        private cvLocalization: CVLocalizationService,
        private filterTextPipe: FilterTextPipe,
    ) { }

    ngOnInit() {

    }

    ngOnChanges(changes: SimpleChanges) {
        let hasSetupValues = false;
        if (changes.options?.currentValue) {
            this.cxOptions = this.options.map((option, index) => ({
                id: option[this.primaryKey],
                name: option[this.label],
                sort: String(index).padStart(7, '0'),
            }));
            this.cxOptions.sort((a, b) => {
                if (a.name > b.name) return 1;
                else if (a.name < b.name) return -1;
                else return 0; 
            });

            if (this.value?.length || this.value?.length === 0) {
                this.setupCxOneSelection();
                hasSetupValues = true;
            }
        }

        if (!hasSetupValues && changes.value?.currentValue) {
            if (this.cxOptions?.length) {
                this.setupCxOneSelection();
            }
        }
    }

    ngAfterViewInit() {
        if (this.matSelects?.length)
            this.setupEmptyState();

        this._matSelectSubscription = this.matSelects.changes.subscribe(() => {
            this.setupEmptyState();
            this.matSelects.forEach(matSelect => {
                matSelect.openedChange.subscribe(opened => {
                    if (opened) {
                        setTimeout(() => {
                            const panel = document.querySelector('.custom-panel-class');
                            if (panel) {
                                this.applyCustomStyles(panel as HTMLElement);
                            }
                        }, 0);
                    }
                });
            });
        });

        this.setupScrollHandler();
    }

    ngOnDestroy() {
        this._scrollableSubscription?.unsubscribe();
        this._matSelectSubscription?.unsubscribe();
    }

    setupCxOneSelection() {
        const selectedValues = new Set(this.value);
        this.cxValues = this.cxOptions
            .filter(o => selectedValues.has(o.id))
            .map(o => ({ id: o.id, name: o.name, sort: o.sort, selected: true }));
    }

    setupEmptyState() {
        // replace "empty" flag of the select to use the actual model value rather than only the options that are visible
        let needsUpdate = false;
        this.matSelects.forEach(select => {
            Object.defineProperty(
                select,
                'empty',
                { get: () => this.showEmptyState && (this.selectAllOnEmpty || this.value.length === 0) }
            );
            needsUpdate = true;
        });

        if (needsUpdate) {
            this.changeDetector.detectChanges();
        }
    }

    setupScrollHandler() {
        // subscribe when one of our scrollable containers scroll
        this._scrollableSubscription = this.scrollDispatcher
            .scrolled()
            .pipe(filter<CdkScrollable>(scrollable => this.cdkVirtualScrollViewPorts.find(viewport => viewport === scrollable) != null))
            .subscribe(_ => {
                // when scrolling we need to update the options if they are selected since they are being re-rendered by the CdkVirtualScroll.
                const selectedItems = new Set(this.value);
                let needsUpdate = false;

                for (const option of this.matOptions) {
                    const selected = selectedItems.has(option.value);

                    if (selected && !option.selected) {
                        option.select();
                        needsUpdate = true;
                    } else if (!selected && option.selected) {
                        option.deselect();
                        needsUpdate = true;
                    }
                }

                if (needsUpdate) {
                    this.changeDetector.detectChanges();
                }
            });
    }

    onOpen() {
        // virtual scroll won't render properly without triggering a scroll to the top
        this.cdkVirtualScrollViewPorts?.forEach(viewport => {
            viewport.scrollToIndex(1);
            viewport.scrollToIndex(0);
            viewport.checkViewportSize();
        });

        setTimeout(() => {
            const panel = document.querySelector('.custom-panel-class');
            if (panel) {
                this.applyCustomStyles(panel as HTMLElement);
            }
        }, 0);

        this.lastChange = null;
        this.opened.emit();
    }

    onClose() {
        const filteredOptions = this.filterTextPipe.transform(this.options, this.filterText, this.label);
        if (filteredOptions.length === 0) this.filterText = '';
        if (this.selectAllOnEmpty && this.value.length === 0) {
            this.selectAll();
        }
        if (this.value.length === 0) this.checked = true;
        this.closed.emit();
    }

    applyCustomStyles(panel: HTMLElement) {
        const selectPanel = document.querySelector('.mat-select-panel') as HTMLElement;
        if (panel) { //Only if such a class exists will the style be applied
            selectPanel.style.maxHeight = '450px';
        }
    }

    selectAll() {
        if (this.value.length >= 0 && this.value.length !== this.options.filter(i => i[this.primaryKey] !== -1).length && this.filterText == ""){
            this.value = this.options.map(o => o[this.primaryKey]);
            this.valueChange.emit(this.value);
            this.selectionChanged.emit({
                value: this.value,
                wasSelectAll: true,
                wasSelectNone: false,
            });
            this.updatedValues = [];
            this.checkBoxStatus = {clickAction: 'check-indeterminate'};
            this.checked = true;
        } else if (!this.showSelectAllOrNoneButtons) {
            this.selectNone();
        }
    }

    selectNone() {
        this.value = [];
        this.valueChange.emit(this.value);
        this.selectionChanged.emit({
            value: this.value,
            wasSelectAll: false,
            wasSelectNone: true,
        });
        this.updatedValues = [];
        this.checkBoxStatus = {clickAction: 'check-indeterminate'};
        this.checked = false;
    }

    selectSearch() {
        const items = new Set(this.updatedValues);
        const filteredItems = new Set(this.options.filter(o => o[this.label].toUpperCase().includes(this.filterText.toUpperCase()) && o[this.primaryKey] !== -1));
        //const newItems = union(items, compareItems);

        //Compare Sets to check if items are already included. If they are remove them if all items returned in search are selected. Select if items returned aren't selected.
        if (setsAreEqual(items, filteredItems)) {
            this.updatedValues = [];
        } else if (isSuperset(items, filteredItems)) {
                this.updatedValues = Array.from(difference(items, filteredItems)).sort();
        } else {
            this.updatedValues = Array.from(union(items, filteredItems)).sort();
        }

        //check if 
        function isSuperset(set, subset) {
            for (const elem of subset) {
              if (!set.has(elem)) {
                return false;
              }
            }
            return true;
          }
        
        //remove items not included in first Set
        function difference(setA, setB) {
            const _difference = new Set(setA);
            for (const elem of setB) {
                _difference.delete(elem);
            }
            return _difference;
        }
        
        //combine Sets
        function union(setA, setB) {
            const _union = new Set(setA);
            for (const elem of setB) {
                _union.add(elem);
            }
            return _union;
        }
        
        //check difference and remove or add items
        function symmetricDifference(setA, setB) {
            const _difference = new Set(setA);
            for (const elem of setB) {
                if (_difference.has(elem)) {
                    _difference.delete(elem);
                } else {
                    _difference.add(elem);
                }
            }
            return _difference;
        }
        
        //check if Sets are equal sizes and have same values
        function setsAreEqual(setA, setB) {
            if (setA.size != setB.size) {
                return false;
            } else if (setA.size > 0) {
                let setC = symmetricDifference(setA, setB);
                if (setC.size > 0)
                    return false;
                else
                    return true;
            } else {
                return false;
            }
        }

        //select items returned in search
        this.value = this.updatedValues.map(o => o[this.primaryKey]);
        this.valueChange.emit(this.value);
        this.selectionChanged.emit({
            value: this.value,
            wasSelectAll: false,
            wasSelectNone: false,
        });
        
        if (this.value.length > 0 && this.value.length !== this.options.filter(i => i[this.primaryKey] !== -1).length)
            this.checkBoxStatus = { clickAction: 'noop'};
        else 
            this.checkBoxStatus = { clickAction: 'check-indeterminate'};
    }

    closeDropdown() {
        if (this.config.isCxOne) {
            if (this.cxOneMultiSelectComponent?.isDropdownOpen)
                this.cxOneMultiSelectComponent.toggleDropdown();
        } else {
            this.matSelectComponent?.close();
        }
    }

    checkIndeterminate(): boolean {
        return (this.value.length > 0 && this.value.length !== this.options.filter(i => i[this.primaryKey] !== -1).length) || (this.value.length == 1 && !this.checked);
    }

    checkChecked(): boolean {
        return (this.value.length > 1 && this.value.length === this.options.filter(i => i[this.primaryKey] !== -1).length && !this.checkIndeterminate()) || (this.options.filter(i => i[this.primaryKey] !== -1).length == 1 && this.value.length === this.options.filter(i => i[this.primaryKey] !== -1).length && this.checked);
    }

    onMatSelectChange(change: MatOptionSelectionChange) {
        if (!change.isUserInput)
            return;

        const id: number = change.source.value;
        const index = this.value.indexOf(id);

        const newValue = [...this.value];
        if (index >= 0)
            newValue.splice(index, 1);
        else
            newValue.push(id);

        this.value = newValue;
        this.valueChange.emit(this.value);
        this.selectionChanged.emit({
            value: this.value,
            wasSelectAll: false,
            wasSelectNone: false,
        });
        this.checked = false;
    }

    onCxOneOpen() {
        this.opened.emit();
        if (this.isInfiniteDropdown) {
            this.setDropdownData();
        }
    }

    onCxOneClose(elementRef = null) {
        const change = this.lastChange;
        if (change !== null) { 
            this.selectionChanged.emit({
                value: this.value,
                wasSelectAll: change.selectAll === true,
                wasSelectNone: change.selectAll === false,
            });
        }
        this.closed.emit();
        if (this.isInfiniteDropdown && elementRef !== null) {
            elementRef.query = "";
            this.setDropdownData();
        }
    }

    onCxOneSelectChange(change: CXOneMultiSelectChanged) {
        this.value = change.selected.map(v => v.id);
        this.valueChange.emit(this.value);
        this.lastChange = change;
    }
    
    setDropdownData() {
        this.skip = 0;
        this.top = 100;
        this.dropdownOptions = [...this.cxOptions.slice(this.skip, this.skip + this.top)];
        this.skip = this.skip + this.top;
        this.dataLimit = this.cxOptions.length;
        this.serverSideOptions.resultsCount = this.cxOptions.length;
    }

    onLoadData() {
        if (this.skip <= this.dataLimit) {
            this.isLoading = true;
            this.dropdownOptions = this.dropdownOptions.concat(this.cxOptions.slice(this.skip, this.skip + this.top));
            this.skip = this.skip + this.top;
            this.isLoading = false;
        }
    }

    onSearch(event) {
        this.isLoading = true;
        this.skip = 0;
        this.top = 50;
        const parsedResults = this.cxOptions;
        let options = [];
        if (this.isEmpty(event.searchQuery)) {
            options = parsedResults;
        } else {
            options = parsedResults.filter((item: any) => {
                return item.name.toLowerCase().search(event.searchQuery.toLowerCase()) !== -1;
            });
        }
        this.serverSideOptions.resultsCount = options.length;
        this.dropdownOptions = [...options.slice(this.skip, this.skip + this.top)];
        this.skip = this.skip + this.top;
        this.dataLimit = options.length;
        this.isLoading = false;
    }

    isEmpty(value) {
        return value === '' || value === undefined || value === null;
    }
}
